// Typed errors raised by the api-client. Callers branch on the class and on `code`, never on
// message text (constitution §4.11: the API answers `{ error: { code, message, details? } }`).

/** Code used when a response is not the JSON the contract promises (review D-R10). */
export const UNEXPECTED_RESPONSE = 'unexpected_response';

export type ErrorDetails = Readonly<Record<string, unknown>>;

/** The API answered, but not with the success the caller asked for. */
export class ApiError extends Error {
  override readonly name = 'ApiError';

  readonly status: number;
  readonly code: string;
  readonly details: ErrorDetails | undefined;

  constructor(status: number, code: string, message: string, details?: ErrorDetails) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** `details.reason` when the API sent one as text (e.g. `missing_app_role`), else undefined. */
  get reason(): string | undefined {
    const reason = this.details?.['reason'];
    return typeof reason === 'string' ? reason : undefined;
  }
}

/**
 * The request never got an HTTP answer: offline, DNS, proxy drop, CORS. Wraps the TypeError that
 * fetch() rejects with, so other TypeErrors (bugs) are not mistaken for network trouble (D-R8).
 */
export class NetworkError extends Error {
  override readonly name = 'NetworkError';

  constructor(cause: unknown) {
    super('The portal could not be reached', { cause });
  }
}

export function isApiError(err: unknown, code?: string): err is ApiError {
  return err instanceof ApiError && (code === undefined || err.code === code);
}

/** True for an answered request with this HTTP status. */
export function hasStatus(err: unknown, status: number): err is ApiError {
  return err instanceof ApiError && err.status === status;
}
